import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, MapPin, Calendar, Users, IndianRupee, User, Phone as PhoneIcon, CheckCircle2, Sparkles, MessageCircle, Plane, Heart, UsersRound, Compass } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DOMESTIC_DESTINATIONS, INTERNATIONAL_DESTINATIONS, COMPANY, saveEnquiry } from '../../mock';

const STORAGE_KEY = 'mct_popup_shown_v1';

const BUDGETS = [
  { value: 'under-10k', label: 'Under ₹10K' },
  { value: '10-20k', label: '₹10K – ₹20K' },
  { value: '20-35k', label: '₹20K – ₹35K' },
  { value: '35-60k', label: '₹35K – ₹60K' },
  { value: '60k+', label: '₹60K+' },
];

const MONTHS = [
  'This Month', 'Next Month', 'In 2-3 Months', 'Later this Year', 'Just Exploring',
];

export default function EnquiryPopup() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [animDir, setAnimDir] = useState('next');
  const [form, setForm] = useState({ tripType: '', destination: '', when: '', people: 2, budget: '', name: '', phone: '' });

  useEffect(() => {
    // Auto-open on first visit
    const seen = sessionStorage.getItem(STORAGE_KEY);
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 4500);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    // Listen for manual trigger
    const handleOpen = () => {
      setOpen(true);
      setStep(1);
    };
    window.addEventListener('mct:openEnquiry', handleOpen);
    return () => window.removeEventListener('mct:openEnquiry', handleOpen);
  }, []);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(STORAGE_KEY, '1');
    setTimeout(() => setStep(1), 400);
  };

  const next = () => { setAnimDir('next'); setStep(s => s + 1); };
  const back = () => { setAnimDir('back'); setStep(s => s - 1); };
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    await saveEnquiry(form);
    close();
    navigate('/thank-you', {
      state: {
        name: form.name,
        phone: form.phone,
        email: '',
        destination: form.destination,
        when: form.when,
        people: String(form.people),
        budget: form.budget,
        source: 'popup',
      },
    });
  };

  const canNext = () => {
    if (step === 1) return !!form.tripType;
    if (step === 2) return !!form.destination;
    if (step === 3) return !!form.when;
    if (step === 4) return !!form.budget;
    if (step === 5) return form.name.trim().length > 1 && form.phone.replace(/\D/g,'').length >= 10;
    return true;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-popup-overlay" onClick={close}>
      <div className="absolute inset-0 bg-brand-blue/70 backdrop-blur-sm"/>
      <div className="relative bg-white rounded-3xl shadow-premium w-full max-w-[640px] overflow-hidden animate-popup-card" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-brand-grey text-brand-blue flex items-center justify-center transition-colors" aria-label="close"><X size={18}/></button>

        {/* Hero strip */}
        <div className="relative h-[120px] bg-gradient-to-br from-brand-blue to-brand-blueLight overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand-orange/30 blur-2xl animate-float"/>
          <div className="absolute top-8 -left-6 w-32 h-32 rounded-full bg-white/10 blur-xl"/>
          <div className="relative h-full px-7 flex flex-col justify-center text-white">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] uppercase text-brand-orange">
              <Sparkles size={12}/> Free Travel Consultation
            </div>
            <h3 className="font-poppins font-bold text-[22px] mt-1.5">Plan Your <span className="text-brand-orange">Dream Trip</span></h3>
          </div>
        </div>

        {/* Progress */}
        <div className="px-7 pt-5">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] font-bold tracking-wider text-brand-blue/55 uppercase">Step {step} of 5</div>
            <div className="text-[11px] text-brand-blue/55">No spam · No obligation</div>
          </div>
          <div className="h-1.5 rounded-full bg-brand-grey overflow-hidden">
            <div className="h-full bg-brand-orange rounded-full transition-all duration-500 ease-out" style={{width: `${(step/5)*100}%`}}/>
          </div>
        </div>

        {/* Steps */}
        <div className="px-7 py-6 min-h-[290px]">
          <div key={step} className={animDir === 'next' ? 'animate-step-in-next' : 'animate-step-in-back'}>
            {step === 1 && <StepTripType form={form} update={update}/>}
            {step === 2 && <StepDestination form={form} update={update}/>}
            {step === 3 && <StepWhen form={form} update={update}/>}
            {step === 4 && <StepBudget form={form} update={update}/>}
            {step === 5 && <StepContact form={form} update={update}/>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-7 pb-7 flex items-center justify-between gap-3 border-t border-brand-grey pt-5">
          <button onClick={back} disabled={step === 1} className={`flex items-center gap-1.5 text-[14px] font-semibold transition-colors ${step === 1 ? 'text-brand-blue/30 cursor-not-allowed' : 'text-brand-blue hover:text-brand-orange'}`}><ChevronLeft size={16}/>Back</button>
          {step < 5 ? (
            <Button onClick={next} disabled={!canNext()} className="bg-brand-orange hover:bg-brand-orangeDark text-white h-11 px-6 rounded-xl font-semibold btn-glow disabled:opacity-50 disabled:cursor-not-allowed">
              Continue <ChevronRight size={16} className="ml-1"/>
            </Button>
          ) : (
            <Button onClick={submit} disabled={!canNext()} className="bg-brand-orange hover:bg-brand-orangeDark text-white h-11 px-6 rounded-xl font-semibold btn-glow disabled:opacity-50 disabled:cursor-not-allowed">
              Get My Quote
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepHeading({ icon: Icon, title, sub }) {
  return (
    <div className="mb-5">
      <div className="w-11 h-11 rounded-xl bg-brand-orange/10 text-brand-orange flex items-center justify-center mb-3"><Icon size={20}/></div>
      <h4 className="font-poppins font-bold text-brand-blue text-[19px] leading-tight">{title}</h4>
      <p className="text-brand-blue/60 text-[13px] mt-1">{sub}</p>
    </div>
  );
}

function StepTripType({ form, update }) {
  const options = [
    { value: 'domestic', label: 'Within India', icon: Compass, accent: 'from-emerald-500 to-emerald-600' },
    { value: 'international', label: 'International', icon: Plane, accent: 'from-sky-500 to-sky-600' },
    { value: 'honeymoon', label: 'Honeymoon', icon: Heart, accent: 'from-rose-500 to-rose-600' },
    { value: 'group', label: 'Group / Family', icon: UsersRound, accent: 'from-amber-500 to-amber-600' },
  ];
  return (
    <div>
      <StepHeading icon={MapPin} title="What kind of trip are you planning?" sub="This helps us match you with the right specialist."/>
      <div className="grid grid-cols-2 gap-3">
        {options.map(o => {
          const Icon = o.icon;
          const selected = form.tripType === o.value;
          return (
            <button key={o.value} onClick={() => update('tripType', o.value)} className={`relative p-4 rounded-xl border-2 text-left transition-all duration-300 group overflow-hidden ${selected ? 'border-brand-orange bg-brand-orange/5' : 'border-brand-greyDark hover:border-brand-blue/30 hover:-translate-y-0.5'}`}>
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${o.accent} text-white flex items-center justify-center shadow-sm transition-transform duration-300 ${selected ? 'scale-105' : 'group-hover:scale-105'}`}>
                <Icon size={20} strokeWidth={2.2} />
              </div>
              <div className="font-poppins font-semibold text-brand-blue text-[14.5px] mt-3">{o.label}</div>
              {selected && <CheckCircle2 size={18} className="absolute top-3 right-3 text-brand-orange"/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepDestination({ form, update }) {
  const isInternational = form.tripType === 'international';
  const list = isInternational ? INTERNATIONAL_DESTINATIONS : DOMESTIC_DESTINATIONS;
  return (
    <div>
      <StepHeading icon={MapPin} title={isInternational ? 'Which country interests you?' : 'Where in India?'} sub="Pick one or scroll for more options."/>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-[230px] overflow-y-auto pr-1">
        {list.map(d => (
          <button key={d.slug} onClick={() => update('destination', d.name)} className={`relative rounded-lg overflow-hidden border-2 h-20 group transition-all ${form.destination === d.name ? 'border-brand-orange ring-2 ring-brand-orange/20' : 'border-transparent hover:border-brand-blue/30'}`}>
            <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/85 to-brand-blue/20"/>
            <div className="absolute inset-x-0 bottom-1.5 px-2 text-white font-poppins font-semibold text-[11.5px] leading-tight text-center line-clamp-2">{d.name}</div>
            {form.destination === d.name && <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-brand-orange text-white flex items-center justify-center"><CheckCircle2 size={12}/></div>}
          </button>
        ))}
        <button onClick={() => update('destination', 'Not Sure Yet')} className={`rounded-lg border-2 h-20 flex items-center justify-center font-poppins font-semibold text-[12px] transition-all ${form.destination === 'Not Sure Yet' ? 'border-brand-orange bg-brand-orange/5 text-brand-orange' : 'border-dashed border-brand-greyDark text-brand-blue/65 hover:border-brand-blue/40'}`}>Help me decide</button>
      </div>
    </div>
  );
}

function StepWhen({ form, update }) {
  return (
    <div>
      <StepHeading icon={Calendar} title="When do you want to travel?" sub="Approximate is fine - we'll fine-tune later."/>
      <div className="grid grid-cols-2 gap-3">
        {MONTHS.map(m => (
          <button key={m} onClick={() => update('when', m)} className={`p-3.5 rounded-xl border-2 text-left transition-all flex items-center justify-between ${form.when === m ? 'border-brand-orange bg-brand-orange/5' : 'border-brand-greyDark hover:border-brand-blue/30'}`}>
            <span className="font-poppins font-semibold text-brand-blue text-[14px]">{m}</span>
            {form.when === m && <CheckCircle2 size={18} className="text-brand-orange"/>}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepBudget({ form, update }) {
  return (
    <div>
      <StepHeading icon={IndianRupee} title="What is your budget per person?" sub="We will find the best value within your range."/>
      <div className="flex items-center justify-between gap-3 p-4 bg-brand-grey rounded-xl mb-4">
        <div className="flex items-center gap-2.5 text-brand-blue text-[14px] font-semibold"><Users size={16} className="text-brand-orange"/>Travelers</div>
        <div className="flex items-center gap-2">
          <button onClick={() => update('people', Math.max(1, form.people - 1))} className="w-8 h-8 rounded-lg bg-white border border-brand-greyDark text-brand-blue font-bold hover:border-brand-orange hover:text-brand-orange transition-colors">−</button>
          <div className="w-8 text-center font-poppins font-bold text-brand-blue">{form.people}</div>
          <button onClick={() => update('people', form.people + 1)} className="w-8 h-8 rounded-lg bg-white border border-brand-greyDark text-brand-blue font-bold hover:border-brand-orange hover:text-brand-orange transition-colors">+</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {BUDGETS.map(b => (
          <button key={b.value} onClick={() => update('budget', b.value)} className={`p-3 rounded-xl border-2 text-left transition-all ${form.budget === b.value ? 'border-brand-orange bg-brand-orange/5' : 'border-brand-greyDark hover:border-brand-blue/30'}`}>
            <div className="font-poppins font-semibold text-brand-blue text-[14px]">{b.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepContact({ form, update }) {
  return (
    <div>
      <StepHeading icon={User} title="Almost done! Where should we send your quote?" sub="We'll send your quote on WhatsApp."/>
      <div className="space-y-3">
        <label className="block">
          <span className="text-[12.5px] font-semibold text-brand-blue/75 mb-1.5 block">Your name</span>
          <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="e.g., Rohan Sharma" className="h-11 rounded-lg"/>
        </label>
        <label className="block">
          <span className="text-[12.5px] font-semibold text-brand-blue/75 mb-1.5 block">WhatsApp number</span>
          <div className="relative">
            <PhoneIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-blue/40"/>
            <Input value={form.phone} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); update('phone', v); }} placeholder="9XXXXXXXXX" maxLength={10} className="h-11 rounded-lg pl-9" inputMode="numeric"/>
          </div>
        </label>
      </div>
      <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-[12.5px] text-emerald-800 flex gap-2">
        <CheckCircle2 size={15} className="mt-0.5 shrink-0"/>
        <span>Your details are safe - we never spam, sell or share them. You'll only hear from a real travel designer.</span>
      </div>
    </div>
  );
}

